import React from 'react';
import { Field } from 'formik';
import ButtonGroup, {
  ButtonConfig,
  LayoutType,
} from 'src/features/common/components/button-group/revamp-button-group';
import { FormLayout, ShowIf, FormRowLayout, MiniAppFocusWrapper } from 'src/components';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import {
  idMask,
  spaceBy4Mask,
  passportMask,
  asylumMask,
  refugeeMask,
} from 'src/helpers/formatting';
import { COUNTRY_CODES } from 'src/services/salesforce-service-helpers/country-codes';
import { StyledDatePicker } from 'src/features/common/components/styled-date-picker/styled-date-picker';
import { IdType, IdentityTypeDirectors, InputField } from 'src/features/common';
import { CREDIT_VETTING_TITLES, MIN_DATE_ALLOWED, TITLES } from 'src/common';
import { PAGE_NAME } from 'src/features/common/types/tealium-service';
import { DirectorIdTypes, IdentityTypeItem } from '../director-details-page';
import './director-info-fields.scss';

export function DirectorInfoFields({
  field,
  idInputFieldPlaceHolderText,
  passportInputFieldPlaceHolderText,
  isMiniProgram,
  isCreditVettingEnabled,
  isForeignNationalsEnabled,
  automationIdPrefix,
}) {
  const pageName = PAGE_NAME.DIRECTOR_DETAILS;

  const southAfricanIdButton: ButtonConfig = {
    automationId: `${automationIdPrefix}_RSA_ID_Button`,
    label: 'RSA ID',
    buttonStyle: DefaultButtonStyle()
      .hasSecondaryAction()
      .hasGreyMode()
      .isExtraLargeSize()
      .isResponsive()
      .build(),
    buttonParams: {
      type: ButtonParamType.RadioButton,
      value: IdType.RSA_ID,
    },
    className: 'RSA-ID-button',
  };

  const passportButton: ButtonConfig = {
    automationId: `${automationIdPrefix}_Passport_Button`,
    label: 'Passport',
    buttonStyle: DefaultButtonStyle()
      .hasSecondaryAction()
      .hasGreyMode()
      .isExtraLargeSize()
      .isResponsive()
      .build(),
    buttonParams: {
      type: ButtonParamType.RadioButton,
      value: IdType.PASSPORT,
    },
    className: 'Passport-button',
  };

  const getIdPlaceholder = (idType: IdentityTypeDirectors) => {
    switch (idType) {
      case IdentityTypeDirectors.RSA_ID:
        return idInputFieldPlaceHolderText;

      case IdentityTypeDirectors.PASSPORT:
        return 'Passport number';

      case IdentityTypeDirectors.REFUGEE:
      case IdentityTypeDirectors.ASYLUM_SEEKER:
        return 'Document ID';
    }
  };

  const getIdMask = (): (string | RegExp)[] | ((val: string) => (string | RegExp)[]) => {
    if (field.value.idType?.value === IdentityTypeDirectors.RSA_ID) return idMask;
    switch (field.value.idType?.value) {
      case IdentityTypeDirectors.PASSPORT:
        return passportMask;
      case IdentityTypeDirectors.ASYLUM_SEEKER:
        return asylumMask;
      case IdentityTypeDirectors.REFUGEE:
        return refugeeMask;
      default:
        return spaceBy4Mask;
    }
  };

  const getExpiryDateLabel = () => {
    if (field.value.idType?.value === IdentityTypeDirectors.PASSPORT) {
      return 'Passport expiry date';
    }

    return 'Document expiry date';
  };

  if (isForeignNationalsEnabled) {
    return (
      <FormLayout>
        <Field
          name={`${field.name}.idType`}
          type="text"
          placeholder="Identity Type"
          items={DirectorIdTypes}
          getItemDisplayValue={(item: IdentityTypeItem) => item.label}
          component={MiniAppFocusWrapper}
          innerComponent={InputField}
          fieldType="select"
          isDark={false}
        />
        <Field
          name={`${field.name}.idNumber`}
          type={field.value.idType?.value === IdentityTypeDirectors.RSA_ID ? 'tel' : 'text'}
          mask={getIdMask()}
          placeholder={getIdPlaceholder(field.value.idType?.value)}
          component={MiniAppFocusWrapper}
          innerComponent={InputField}
          fieldType="masked"
          guide={!isMiniProgram && field.value.idType?.value === IdentityTypeDirectors.RSA_ID}
          className="top-field bottom-field light"
          automationid={`${automationIdPrefix}_ID_Number`}
          pageName={pageName}
        />

        {isCreditVettingEnabled && (
          <FormRowLayout>
            {[
              () => (
                <Field
                  name={`${field.name}.title`}
                  type="text"
                  placeholder="Title"
                  items={CREDIT_VETTING_TITLES}
                  getItemDisplayValue={(item) => {
                    return item;
                  }}
                  isComboField={true}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="select"
                  isDark={false}
                  automationid={`${automationIdPrefix}_Title`}
                  className="top-field"
                  pageName={pageName}
                />
              ),
              () => (
                <Field
                  name={`${field.name}.initials`}
                  type="text"
                  placeholder="Initials"
                  isComboField={true}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="standard"
                  className="light"
                  automationid={`${automationIdPrefix}_Initials`}
                  pageName={pageName}
                />
              ),
            ]}
          </FormRowLayout>
        )}

        {(field.value.idType?.value !== IdentityTypeDirectors.RSA_ID || isCreditVettingEnabled) && (
          <Field
            name={`${field.name}.firstName`}
            type="text"
            placeholder="First name"
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            fieldType="standard"
            className="light"
            automationid={`${automationIdPrefix}_First_Name`}
            pageName={pageName}
          />
        )}

        {(field.value.idType?.value !== IdentityTypeDirectors.RSA_ID || isCreditVettingEnabled) && (
          <Field
            name={`${field.name}.surname`}
            type="text"
            placeholder="Surname"
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            fieldType="standard"
            className="light"
            automationid={`${automationIdPrefix}_Surname`}
            pageName={pageName}
          />
        )}

        {isCreditVettingEnabled && field.value.idType?.value !== IdentityTypeDirectors.RSA_ID && (
          <Field
            name={`${field.name}.gender`}
            type="text"
            placeholder="Gender"
            items={['Male', 'Female']}
            getItemDisplayValue={(item) => item}
            isComboField={true}
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            fieldType="select"
            automationid={`${automationIdPrefix}_Gender`}
            pageName={pageName}
          />
        )}

        {field.value.idType?.value !== IdentityTypeDirectors.RSA_ID && (
          <Field
            name={`${field.name}.countryOfOrigin`}
            type="text"
            placeholder="Country of origin"
            items={COUNTRY_CODES}
            getItemDisplayValue={(item) => item}
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            fieldType="select"
            isDark={false}
          />
        )}

        {field.value.idType?.value !== IdentityTypeDirectors.RSA_ID && (
          <FormRowLayout>
            {[
              () => (
                <Field
                  name={`${field.name}.passportExpiryDate`}
                  fieldName={getExpiryDateLabel()}
                  component={MiniAppFocusWrapper}
                  innerComponent={StyledDatePicker}
                  lightMode={true}
                />
              ),
              () => (
                <Field
                  name={`${field.name}.dateOfBirth`}
                  fieldName="Date of birth"
                  component={MiniAppFocusWrapper}
                  innerComponent={StyledDatePicker}
                  isLightMode={true}
                  minDate={MIN_DATE_ALLOWED}
                  maxDate={new Date()}
                />
              ),
            ]}
          </FormRowLayout>
        )}
      </FormLayout>
    );
  }

  return !isCreditVettingEnabled ? (
    <Field
      name={`${field.name}.idNumber`}
      type="tel"
      mask={idMask}
      placeholder={getIdPlaceholder(field.value.idType?.value)}
      component={MiniAppFocusWrapper}
      innerComponent={InputField}
      fieldType="masked"
      guide={!isMiniProgram}
      className="light"
      automationid={`${automationIdPrefix}_ID_Number`}
      pageName={pageName}
    />
  ) : (
    <div>
      <ShowIf condition={isForeignNationalsEnabled}>
        <div className="id-type-field-layout">
          <Field
            name={`${field.name}.idType`}
            component={ButtonGroup}
            buttons={[southAfricanIdButton, passportButton]}
            layoutType={LayoutType.FULL_WIDTH}
          />
        </div>
      </ShowIf>

      <FormRowLayout>
        {[
          () => (
            <Field
              name={`${field.name}.title`}
              type="text"
              placeholder="Title"
              items={TITLES}
              getItemDisplayValue={(item) => {
                return item;
              }}
              isComboField={true}
              component={MiniAppFocusWrapper}
              innerComponent={InputField}
              fieldType="select"
              isDark={false}
              automationid={`${automationIdPrefix}_Title`}
              className="top-field"
              pageName={pageName}
            />
          ),
          () => (
            <Field
              name={`${field.name}.initials`}
              type="text"
              placeholder="Initials"
              isComboField={true}
              component={MiniAppFocusWrapper}
              innerComponent={InputField}
              fieldType="standard"
              className="light"
              automationid={`${automationIdPrefix}_Initials`}
              pageName={pageName}
            />
          ),
        ]}
      </FormRowLayout>
      <FormLayout>
        <Field
          name={`${field.name}.firstName`}
          type="text"
          placeholder="First name"
          component={MiniAppFocusWrapper}
          innerComponent={InputField}
          fieldType="standard"
          className="light"
          automationid={`${automationIdPrefix}_First_Name`}
          pageName={pageName}
        />

        <Field
          name={`${field.name}.surname`}
          type="text"
          placeholder="Surname"
          component={MiniAppFocusWrapper}
          innerComponent={InputField}
          fieldType="standard"
          className="light"
          automationid={`${automationIdPrefix}_Surname`}
          pageName={pageName}
        />

        <Field
          name={`${field.name}.idNumber`}
          type="tel"
          mask={idMask}
          placeholder={idInputFieldPlaceHolderText}
          component={MiniAppFocusWrapper}
          innerComponent={InputField}
          fieldType="masked"
          guide={!isMiniProgram}
          className="light"
          automationid={`${automationIdPrefix}_ID_Number`}
          pageName={pageName}
        />

        <ShowIf condition={field.value.idType?.value === IdType.PASSPORT}>
          <Field
            name={`${field.name}.passportNumber`}
            type="text"
            placeholder={passportInputFieldPlaceHolderText}
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            fieldType="standard"
            className="light"
            automationid={`${automationIdPrefix}_Passport_Number`}
            pageName={pageName}
          />
        </ShowIf>

        {/* @ts-ignore */}
        <ShowIf condition={field.value.idType?.value === IdType.PASSPORT}>
          <FormRowLayout>
            {[
              () => (
                <Field
                  name={`${field.name}.dateOfBirth`}
                  fieldName="Date of birth"
                  component={MiniAppFocusWrapper}
                  innerComponent={StyledDatePicker}
                  isLightMode={true}
                  automationid={`${automationIdPrefix}_Date_Of_Birth`}
                  pageName={PAGE_NAME.DIRECTOR_DETAILS}
                />
              ),
              () => (
                <Field
                  name={`${field.name}.gender`}
                  type="text"
                  placeholder="Gender"
                  items={['Male', 'Female']}
                  getItemDisplayValue={(item) => {
                    return item;
                  }}
                  isComboField={true}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="select"
                  automationid={`${automationIdPrefix}_Gender`}
                  pageName={pageName}
                />
              ),
            ]}
          </FormRowLayout>
        </ShowIf>

        {/* @ts-ignore */}
        <ShowIf condition={field.value.idType?.value === IdType.PASSPORT}>
          <Field
            name={`${field.name}.passportExpiryDate`}
            fieldName="Passport expiry date"
            component={MiniAppFocusWrapper}
            innerComponent={StyledDatePicker}
            lightMode={true}
            automationid={`${automationIdPrefix}_Passport_Expiry_Date`}
            pageName={PAGE_NAME.DIRECTOR_DETAILS}
          />
        </ShowIf>

        {/* @ts-ignore */}
        <ShowIf condition={field.value.idType?.value === IdType.PASSPORT}>
          <Field
            name={`${field.name}.countryOfOrigin`}
            type="text"
            placeholder="Country of origin"
            items={COUNTRY_CODES}
            getItemDisplayValue={(item) => {
              return item;
            }}
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            fieldType="select"
            isDark={false}
            automationid={`${automationIdPrefix}_Country_Of_Origin`}
            pageName={pageName}
          />
        </ShowIf>
      </FormLayout>
    </div>
  );
}
